// FiltersSheet — Bottom sheet de filtros (FASE 2)
// Comparte estado vía URL params con las chips rápidas de /eventos
import React, { useEffect, useMemo, useState } from 'react';
import { COLORS } from './designSystem';
import { Chip, BottomSheet } from './components';
import { EventsService } from './EventsService';
import { getStoredAge, setStoredAge } from './userStore';

const ESTILOS = ['Techno', 'House', 'Reggaeton', 'Comercial', 'Latino', 'Hip hop'];
const TIPOS = [
  { k: 'discoteca', label: 'Discoteca' },
  { k: 'universitaria', label: 'Fiesta universitaria' },
  { k: 'terraza', label: 'Terraza' },
  { k: 'viaje', label: 'Viaje' },
];
const CUANDO = [
  { k: 'tarde', label: 'Tarde' },      // 17:00 - 21:59
  { k: 'noche', label: 'Noche' },      // 22:00 - 02:59
  { k: 'madrugada', label: 'Madrugada' }, // 03:00 - 07:00
];

// Parse / serialize filters from URL params
export function filtersFromParams(params) {
  return {
    estilos: (params.get('estilos') || '').split(',').filter(Boolean),
    cuando: params.get('cuando') || '',
    precioMax: params.get('precioMax') !== null ? Number(params.get('precioMax')) : 50,
    tipoPlan: params.get('tipoPlan') || '',
    soloMiEdad: params.get('edad') === '1',
  };
}

export function filtersToParams(f, extras = {}) {
  const out = { ...extras };
  if (f.estilos && f.estilos.length) out.estilos = f.estilos.join(',');
  if (f.cuando) out.cuando = f.cuando;
  if (f.precioMax !== 50) out.precioMax = String(f.precioMax);
  if (f.tipoPlan) out.tipoPlan = f.tipoPlan;
  if (f.soloMiEdad) out.edad = '1';
  return out;
}

export const FiltersSheet = ({ open, onClose, initial, onApply, allEventsForCount = [] }) => {
  const [draft, setDraft] = useState(initial);
  const [askAge, setAskAge] = useState(false);
  const wasOpen = React.useRef(false);
  // Solo resetea el draft cuando el sheet pasa de cerrado→abierto, no en cada re-render del padre.
  useEffect(() => {
    if (open && !wasOpen.current) { setDraft(initial); setAskAge(false); }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key, value) => setDraft((p) => ({
    ...p,
    [key]: Array.isArray(p[key])
      ? (p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value])
      : (p[key] === value ? '' : value),
  }));

  const applyAgeToggle = () => {
    if (!draft.soloMiEdad) {
      const existing = getStoredAge();
      if (!existing) { setAskAge(true); return; }
    }
    setDraft((p) => ({ ...p, soloMiEdad: !p.soloMiEdad }));
  };

  const matches = useMemo(() => {
    const age = getStoredAge();
    return allEventsForCount.filter((e) => {
      // estilos (OR)
      if (draft.estilos.length && !(e.estilos || []).some((s) => draft.estilos.includes(s))) return false;
      // tipo plan
      if (draft.tipoPlan && e.tipo_plan !== draft.tipoPlan) return false;
      // precio
      if (draft.precioMax === 0) { if ((e.price || 0) !== 0) return false; }
      else if (draft.precioMax < 50 && (e.price || 0) > draft.precioMax) return false;
      // cuando
      if (draft.cuando && e.fecha_inicio) {
        const h = new Date(e.fecha_inicio).getHours();
        if (draft.cuando === 'tarde' && !(h >= 17 && h <= 21)) return false;
        if (draft.cuando === 'noche' && !((h >= 22 && h <= 23) || (h >= 0 && h <= 2))) return false;
        if (draft.cuando === 'madrugada' && !(h >= 3 && h <= 7)) return false;
      }
      // edad
      if (draft.soloMiEdad && age && (e.edad_minima || 18) > age) return false;
      return true;
    }).length;
  }, [draft, allEventsForCount]);

  const clearAll = () => setDraft({ estilos: [], cuando: '', precioMax: 50, tipoPlan: '', soloMiEdad: false });

  return (
    <BottomSheet open={open} onClose={onClose} testid="filters-sheet" title={null}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-xl font-bold">Filtros</h3>
        <div className="flex items-center gap-2">
          <button onClick={clearAll} className="text-sm font-semibold" style={{ color: COLORS.coral }} data-testid="filters-clear-all">Borrar todo</button>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.surface }} data-testid="filters-close" aria-label="Cerrar">✕</button>
        </div>
      </div>

      {/* Age toggle */}
      <div className="rounded-2xl p-4 mb-5" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">Solo eventos a los que puedo entrar</p>
            <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>Según tu edad (+{getStoredAge() || 18}), guardada en tu perfil</p>
          </div>
          <button onClick={applyAgeToggle} className="relative w-12 h-7 rounded-full transition-colors" style={{ backgroundColor: draft.soloMiEdad ? COLORS.primary : '#2a2a30' }} data-testid="filters-age-toggle">
            <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform" style={{ transform: draft.soloMiEdad ? 'translateX(20px)' : 'translateX(0)' }} />
          </button>
        </div>
        {askAge && (
          <div className="mt-3 flex gap-2" data-testid="filters-age-picker">
            <button onClick={() => { setStoredAge(18); setAskAge(false); setDraft((p) => ({ ...p, soloMiEdad: true })); }} className="flex-1 py-2 rounded-full text-white font-semibold text-sm" style={{ backgroundColor: COLORS.primary }}>+18</button>
            <button onClick={() => { setStoredAge(21); setAskAge(false); setDraft((p) => ({ ...p, soloMiEdad: true })); }} className="flex-1 py-2 rounded-full text-white font-semibold text-sm" style={{ backgroundColor: COLORS.primary }}>+21</button>
          </div>
        )}
      </div>

      {/* Cuando */}
      <p className="text-xs font-bold tracking-wider mb-2.5" style={{ color: COLORS.textMuted }}>CUÁNDO</p>
      <div className="flex gap-2 flex-wrap mb-5">
        {CUANDO.map((c) => (<Chip key={c.k} active={draft.cuando === c.k} onClick={() => toggle('cuando', c.k)} data-testid={`filter-cuando-${c.k}`}>{c.label}</Chip>))}
      </div>

      {/* Precio */}
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-xs font-bold tracking-wider" style={{ color: COLORS.textMuted }}>PRECIO</p>
        <span className="text-white font-bold text-sm" data-testid="filters-price-label">{draft.precioMax === 0 ? 'Gratis' : draft.precioMax >= 50 ? '50 €+' : `Hasta ${draft.precioMax} €`}</span>
      </div>
      <input type="range" min={0} max={50} step={1} value={draft.precioMax} onChange={(e) => setDraft((p) => ({ ...p, precioMax: Number(e.target.value) }))} className="w-full h-2 appearance-none mb-5" style={{ accentColor: COLORS.primary }} data-testid="filters-price-slider" />

      {/* Música */}
      <p className="text-xs font-bold tracking-wider mb-2.5" style={{ color: COLORS.textMuted }}>MÚSICA</p>
      <div className="flex gap-2 flex-wrap mb-5">
        {ESTILOS.map((s) => (<Chip key={s} active={draft.estilos.includes(s)} onClick={() => toggle('estilos', s)} data-testid={`filter-estilo-${s.toLowerCase().replace(/\s/g, '-')}`}>{s}</Chip>))}
      </div>

      {/* Tipo */}
      <p className="text-xs font-bold tracking-wider mb-2.5" style={{ color: COLORS.textMuted }}>TIPO DE PLAN</p>
      <div className="flex gap-2 flex-wrap mb-8">
        {TIPOS.map((t) => (<Chip key={t.k} active={draft.tipoPlan === t.k} onClick={() => toggle('tipoPlan', t.k)} data-testid={`filter-tipo-${t.k}`}>{t.label}</Chip>))}
      </div>

      <button onClick={() => onApply(draft)} className="w-full py-3.5 rounded-full text-white font-bold" style={{ backgroundColor: COLORS.primary }} data-testid="filters-apply">
        Ver {matches} eventos
      </button>
    </BottomSheet>
  );
};
